/**
 * Pure, React-free helpers for the Sprint Board domain: id generation, blank
 * draft factories, validation, and small projections. No I/O, no React.
 */

import { v4 as uuid } from 'uuid';
import type {
  CommonTask,
  HistoricalEstimate,
  Sprint,
  SprintDraft,
  SprintStatus,
  TaskUpdate,
  TaskUpdateDraft,
} from './types';

/** Deterministic document id for a new sprint. */
export function sprintDocId(): string {
  return `sprint_${uuid()}`;
}

/** Deterministic document id for a new task update. */
export function taskUpdateDocId(): string {
  return `update_${uuid()}`;
}

/** Deterministic document id for a new historical estimate. */
export function historicalDocId(): string {
  return `hist_${uuid()}`;
}

/** Deterministic id for a new common / all-hands task. */
export function commonTaskId(): string {
  return `common_${uuid()}`;
}

/** Coerces a value to a non-negative finite number (missing/invalid → 0). */
function nonNegativeNumber(n: unknown): number {
  return typeof n === 'number' && Number.isFinite(n) && n > 0 ? n : 0;
}

/**
 * The total per-member bandwidth reduction from a sprint's common / all-hands
 * tasks: the sum of every task's {@link CommonTask.daysPerMember}. This is the
 * `commonReductionDays` input the capacity engine subtracts from every member's
 * bandwidth (see {@link ../engine/capacity}). Missing/invalid values contribute
 * 0; the result is rounded to 2 decimals.
 */
export function commonReductionDays(commonTasks?: CommonTask[]): number {
  const total = (commonTasks ?? []).reduce(
    (sum, t) => sum + nonNegativeNumber(t.daysPerMember),
    0,
  );
  return Math.round(total * 100) / 100;
}

/**
 * A member's on-call days for a sprint, read from the sprint's
 * {@link Sprint.oncallDaysByAlias} map and CLAMPED to [0, workingDays] (on-call
 * can never exceed the sprint's working days). Missing/invalid → 0.
 */
export function memberOncallDays(
  oncallDaysByAlias: Record<string, number> | undefined,
  alias: string,
  workingDays: number,
): number {
  const raw = oncallDaysByAlias?.[alias];
  const value = nonNegativeNumber(raw);
  const cap = nonNegativeNumber(workingDays);
  return Math.min(value, cap);
}

/** Sprint status metadata in lifecycle order. */
export const SPRINT_STATUSES: { value: SprintStatus; label: string }[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'active', label: 'Active' },
  { value: 'complete', label: 'Complete' },
];

/** True when a string is a recognized sprint status. */
export function isSprintStatus(value: string): value is SprintStatus {
  return SPRINT_STATUSES.some((s) => s.value === value);
}

/** Human-readable label for a sprint status. */
export function sprintStatusLabel(status: SprintStatus): string {
  return SPRINT_STATUSES.find((s) => s.value === status)?.label ?? status;
}

/** Creates an empty sprint draft for the create form (defaults to planning). */
export function blankSprintDraft(): SprintDraft {
  return {
    name: '',
    startDate: '',
    endDate: '',
    teamId: undefined,
    goal: '',
    status: 'planning',
  };
}

/** Projects an existing sprint into an editable draft. */
export function draftFromSprint(sprint: Sprint): SprintDraft {
  return {
    name: sprint.name,
    startDate: sprint.startDate,
    endDate: sprint.endDate,
    teamId: sprint.teamId,
    goal: sprint.goal ?? '',
    status: sprint.status,
  };
}

/**
 * Validates a sprint draft for save. Returns human-readable problems; an empty
 * list means valid. Dates must parse and end must not precede start.
 */
export function validateSprintDraft(draft: SprintDraft): string[] {
  const problems: string[] = [];
  if (!draft.name.trim()) problems.push('Sprint name is required.');
  if (!draft.startDate) problems.push('Start date is required.');
  if (!draft.endDate) problems.push('End date is required.');
  if (draft.startDate && draft.endDate) {
    const start = Date.parse(draft.startDate);
    const end = Date.parse(draft.endDate);
    if (Number.isNaN(start)) problems.push('Start date is invalid.');
    if (Number.isNaN(end)) problems.push('End date is invalid.');
    if (!Number.isNaN(start) && !Number.isNaN(end) && end < start) {
      problems.push('End date must be on or after the start date.');
    }
  }
  if (!isSprintStatus(draft.status)) problems.push('A valid status is required.');
  return problems;
}

/**
 * Counts the working days (Mon–Fri) in a sprint's date range, inclusive of both
 * the start and end dates. Weekends are excluded; no holiday calendar (V1 keeps
 * it simple). Returns 0 when either date is missing/invalid or the range is
 * inverted. Iteration is capped defensively so a nonsense range can't hang.
 *
 * This is the working-days input the capacity engine multiplies by team size to
 * get team capacity (see {@link ../engine/capacity}).
 */
export function sprintWorkingDays(startDate: string, endDate: string): number {
  if (!startDate || !endDate) return 0;
  const start = new Date(`${startDate}T00:00:00Z`);
  const end = new Date(`${endDate}T00:00:00Z`);
  const startMs = start.getTime();
  const endMs = end.getTime();
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) return 0;

  const DAY_MS = 24 * 60 * 60 * 1000;
  const MAX_DAYS = 2000; // ~5.5 years — a hard guard against runaway ranges.
  let count = 0;
  const cursor = new Date(startMs);
  for (let i = 0; i <= MAX_DAYS; i++) {
    if (cursor.getTime() > endMs) break;
    const dow = cursor.getUTCDay(); // 0 = Sunday, 6 = Saturday
    if (dow !== 0 && dow !== 6) count++;
    cursor.setTime(cursor.getTime() + DAY_MS);
  }
  return count;
}

/**
 * The "Day X of Y" position of a sprint, computed from its calendar range.
 *
 * `total` is the inclusive calendar length of the sprint in days (start and end
 * both counted). `day` is today's 1-based position within that range, CLAMPED:
 *   • 0        — the sprint has not started yet (today is before the start).
 *   • 1..total — today's day number within the sprint.
 *   • total    — today is on/after the end date (the sprint has run its course).
 *
 * Returns { day: 0, total: 0 } when either date is missing/invalid or inverted.
 * `now` is injectable for deterministic testing (defaults to the current date).
 */
export function sprintDayInfo(
  startDate: string,
  endDate: string,
  now: Date = new Date(),
): { day: number; total: number } {
  if (!startDate || !endDate) return { day: 0, total: 0 };
  // Treat the date-only strings as CALENDAR days and compare them against the
  // user's LOCAL calendar day. Each day is mapped to a canonical epoch via
  // Date.UTC(y, m, d) purely as a stable ordinal — this keeps day math
  // DST-immune and, crucially, frames "today" in the viewer's local calendar
  // (not UTC), so a user behind/ahead of UTC gets the correct Day X of Y.
  const startMs = calendarDayMs(startDate);
  const endMs = calendarDayMs(endDate);
  if (Number.isNaN(startMs) || Number.isNaN(endMs) || endMs < startMs) {
    return { day: 0, total: 0 };
  }
  const DAY_MS = 24 * 60 * 60 * 1000;
  const total = Math.floor((endMs - startMs) / DAY_MS) + 1;
  // "Today" as the viewer's LOCAL calendar day, mapped to the same ordinal.
  const nowMs = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  if (nowMs < startMs) return { day: 0, total };
  if (nowMs >= endMs) return { day: total, total };
  const day = Math.floor((nowMs - startMs) / DAY_MS) + 1;
  return { day: Math.max(0, Math.min(total, day)), total };
}

/**
 * Maps a date-only string (`YYYY-MM-DD`) to a stable day ordinal via
 * `Date.UTC`, treating it as a calendar day regardless of timezone. Returns
 * `NaN` when the string is not a parseable `YYYY-MM-DD` date.
 */
function calendarDayMs(dateStr: string): number {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(dateStr);
  if (!m) return Number.NaN;
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const ms = Date.UTC(year, month - 1, day);
  // Reject impossible dates (e.g. 2024-13-40) that Date.UTC would roll over.
  const d = new Date(ms);
  if (
    d.getUTCFullYear() !== year ||
    d.getUTCMonth() !== month - 1 ||
    d.getUTCDate() !== day
  ) {
    return Number.NaN;
  }
  return ms;
}

/**
 * Whole days a task has been blocked, from its blocker's `since` timestamp to
 * `now`. Returns 0 when there is no usable timestamp. `now` is injectable for
 * deterministic testing.
 */
export function blockedForDays(
  since: string | undefined,
  now: Date = new Date(),
): number {
  if (!since) return 0;
  const t = Date.parse(since);
  if (Number.isNaN(t)) return 0;
  const DAY_MS = 24 * 60 * 60 * 1000;
  return Math.max(0, Math.floor((now.getTime() - t) / DAY_MS));
}

/** Sorts sprints newest-first by start date, then creation time. */
export function sortSprintsByRecency(sprints: Sprint[]): Sprint[] {
  return sprints
    .slice()
    .sort(
      (a, b) =>
        (b.startDate ?? '').localeCompare(a.startDate ?? '') ||
        (b.createdAt ?? '').localeCompare(a.createdAt ?? ''),
    );
}

/** Creates a blank task-update draft for the standup form. */
export function blankTaskUpdateDraft(taskId = ''): TaskUpdateDraft {
  return {
    taskId,
    completed: '',
    next: '',
    blocker: undefined,
    status: 'on_track',
  };
}

/** Validates a task-update draft. Returns human-readable problems. */
export function validateTaskUpdateDraft(draft: TaskUpdateDraft): string[] {
  const problems: string[] = [];
  if (!draft.taskId.trim()) problems.push('A task is required.');
  if (!draft.completed.trim() && !draft.next.trim()) {
    problems.push('Provide what you completed and/or what is next.');
  }
  return problems;
}

/** Sorts task updates newest-first. */
export function sortUpdatesByRecency(updates: TaskUpdate[]): TaskUpdate[] {
  return updates
    .slice()
    .sort((a, b) => (b.createdAt ?? '').localeCompare(a.createdAt ?? ''));
}

/** Sorts historical estimates newest-first by record time. */
export function sortHistoryByRecency(
  records: HistoricalEstimate[],
): HistoricalEstimate[] {
  return records
    .slice()
    .sort((a, b) => (b.recordedAt ?? '').localeCompare(a.recordedAt ?? ''));
}
